<template>
  <f7-page class="manage-feed-page withMainToolbar" name="manage-feed">
    <f7-navbar sliding>
      <f7-nav-left>
        <f7-link @click="f7router.back()">
          <f7-icon f7="arrow_left" size="18" />
        </f7-link>
      </f7-nav-left>
      <f7-nav-title>{{ $t('feed.manageTitle') }}</f7-nav-title>
    </f7-navbar>

    <div v-if="manageableItems.length === 0" class="mf-empty">
      <f7-icon f7="rectangle_stack" size="28" color="#c0c0c0" />
      <p>{{ $t('feed.noManageableItems') }}</p>
    </div>

    <div v-else class="mf-list">
      <div v-for="item in manageableItems" :key="item.id" class="mf-row">
        <div class="mf-avatar" :style="avatarStyle(item)">{{ initials(item) }}</div>
        <div class="mf-body">
          <div class="mf-title">
            {{ item.headline || item.text || item.poll?.title || item.poll?.id || '' }}
          </div>
          <div class="mf-meta">
            <span class="mf-chip" :class="'mf-chip--' + item.type.toLowerCase()">{{ typeLabel(item) }}</span>
            <span v-if="item.groupName" class="mf-group">{{ item.groupName }}</span>
            <span v-if="item.timeAgo" class="mf-time">{{ item.timeAgo }}</span>
          </div>
        </div>
        <div class="mf-actions">
          <f7-link icon-f7="pencil" :tooltip="$t('common.edit')" @click="dialogsRef.openEdit(item)"></f7-link>
          <f7-link icon-f7="trash" color="red" :tooltip="$t('common.delete')" @click="dialogsRef.confirmDelete(item)"></f7-link>
        </div>
      </div>
    </div>

    <ManageFeedDialogs ref="dialogsRef" @updated="onUpdated" @deleted="onDeleted" />
  </f7-page>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useFeedStore } from '../../js/pinia-stores/feed.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import { useAccountStore } from '../../js/pinia-stores/account.js'
import { useMessageStore } from '../../js/pinia-stores/message.js'
import { canManageItem, isGroupAdmin } from '../../js/feedManageUtils.js'
import ManageFeedDialogs from './ManageFeedDialogs.vue'

const props = defineProps({
  f7route: Object,
  f7router: Object,
})

const feedStore = useFeedStore()
const keycloakStore = useKeycloakStore()
const accountStore = useAccountStore()
const messageStore = useMessageStore()

const dialogsRef = ref(null)

const currentUserId = computed(
  () =>
    keycloakStore.subject ||
    keycloakStore.tokenParsed?.sub ||
    accountStore.userAccount?.userId ||
    ''
)

const manageableItems = computed(() => {
  const accountGroups = accountStore.userGroupList || []
  const messageGroups = messageStore.groupList || []
  return feedStore.items.filter((item) => {
    if (item.type === 'REPOST' || item.type === 'SPONSORED_POST') return false
    return canManageItem(item, currentUserId.value, (it) =>
      isGroupAdmin(it.groupId, currentUserId.value, accountGroups, messageGroups)
    )
  })
})

function onUpdated(item) {
  feedStore.updateItem(item)
}

function onDeleted(itemId) {
  feedStore.removeItem(itemId)
}

function typeLabel(item) {
  if (item.type === 'POLL' || item.type === 'SPONSORED_POLL') return 'Poll'
  return 'Post'
}

function initials(item) {
  const name = item.authorName || ''
  return name.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase() || '?'
}

function avatarStyle(item) {
  const colors = ['#1A6B42', '#3B4FCA', '#C9A84C', '#C0440A', '#6B46C1']
  const hash = (item.authorName || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return { background: colors[hash % colors.length], color: '#fff' }
}
</script>

<style scoped>
.mf-list {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mf-row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--f7-page-bg-color, #fff);
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 10px;
  padding: 10px 12px;
}
.mf-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}
.mf-body {
  flex: 1;
  min-width: 0;
}
.mf-title {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.mf-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.mf-chip {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 8px;
  flex-shrink: 0;
}
.mf-chip--post { background: #EAF3DE; color: #27500A; }
.mf-chip--poll, .mf-chip--sponsored_poll { background: #EBF0FC; color: #0C447C; }
.mf-group { font-weight: 500; }
.mf-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.mf-empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--f7-on-surface-variant, #8e8e93);
  font-size: 13px;
}
.mf-empty p {
  margin-top: 8px;
}
</style>
